import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Stack,
  Text,
  Badge,
  SimpleGrid
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { apiGet } from '../api.js';
import { useAuth } from '../context/AuthContext';
import PaymentModal from '../components/PaymentModal.jsx';

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const Ledger = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewArticle, setViewArticle] = useState(null);
  const dropdownRef = useRef(null);

  // Payment editing
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentArticle, setPaymentArticle] = useState(null);

  useEffect(() => {
    const loadClients = async () => {
      try {
        const res = await apiGet('/clients');
        setClients(res.clients || []);
      } catch (err) {
        setError(err.message || 'Failed to load clients');
      }
    };
    loadClients();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredClients = (clients || []).filter(c =>
    (c && c.name || '').toLowerCase().includes((clientSearch || '').toLowerCase())
  );

  const selectClient = async (client) => {
    setSelectedClient(client);
    setClientSearch(client.name);
    setIsDropdownOpen(false);
    setLoading(true);
    setError('');
    try {
      const res = await apiGet(`/articles?clientId=${client._id}`);
      setArticles(res.articles || []);
    } catch (err) {
      setError(err.message || 'Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const refreshArticles = async () => {
    if (!selectedClient) return;
    try {
      const res = await apiGet(`/articles?clientId=${selectedClient._id}`);
      setArticles(res.articles || []);
    } catch (err) {
      setError(err.message || 'Failed to refresh');
    }
  };

  const clearSelection = () => {
    setSelectedClient(null);
    setClientSearch('');
    setArticles([]);
    setViewArticle(null);
  };

  // Summary calculations
  const activeArticles = articles.filter(a => !a.released);
  const releasedArticles = articles.filter(a => a.released);
  const totalActivePrincipal = activeArticles.reduce((sum, a) => sum + (a.totalPrincipal || a.amount || 0), 0);

  return (
    <Stack spacing={{ base: 4, md: 6 }}>
      <Heading size={{ base: 'md', md: 'lg' }}>Ledger</Heading>

      {/* Client Search */}
      <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={{ base: 3, md: 4 }}>
        <Text fontSize="sm" fontWeight="bold" mb={2}>Search Client</Text>
        <Flex gap={2}>
          <Box flex={1} position="relative" ref={dropdownRef}>
            <Input
              placeholder="Type client name..."
              value={clientSearch}
              onChange={(e) => {
                setClientSearch(e.target.value);
                setIsDropdownOpen(true);
                if (!e.target.value) {
                  clearSelection();
                }
              }}
              onFocus={() => setIsDropdownOpen(true)}
            />
            {isDropdownOpen && clientSearch && (
              <Box
                position="absolute"
                top="100%"
                left={0}
                right={0}
                zIndex={10}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                boxShadow="lg"
                maxH="200px"
                overflowY="auto"
                as="ul"
                listStyleType="none"
                m={0}
                p={0}
              >
                {filteredClients.map(client => (
                  <Box
                    as="li"
                    key={client._id}
                    px={4}
                    py={2}
                    _hover={{ bg: 'blue.50', cursor: 'pointer' }}
                    onClick={() => selectClient(client)}
                    fontWeight={selectedClient?._id === client._id ? 'bold' : 'normal'}
                    bg={selectedClient?._id === client._id ? 'blue.50' : 'white'}
                  >
                    {client.name}
                    {client.contact && <Text as="span" fontSize="xs" color="gray.500" ml={2}>({client.contact})</Text>}
                  </Box>
                ))}
                {filteredClients.length === 0 && (
                  <Box as="li" px={4} py={2} color="gray.500" fontSize="sm">
                    No clients found.
                  </Box>
                )}
              </Box>
            )}
          </Box>
          {selectedClient && (
            <Button variant="outline" size="sm" onClick={clearSelection}>Clear</Button>
          )}
        </Flex>
      </Box>

      {error && <Text color="red.500">{error}</Text>}

      {loading && <Text color="gray.500">Loading...</Text>}

      {/* Client Summary */}
      {selectedClient && !loading && (
        <>
          <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={{ base: 3, md: 4 }}>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <Box>
                <Text fontSize="xs" color="gray.500">Client</Text>
                <Text fontWeight="bold">{selectedClient.name}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">Total Articles</Text>
                <Text fontWeight="bold">{articles.length}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">Active / Released</Text>
                <Text fontWeight="bold">{activeArticles.length} / {releasedArticles.length}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">Total Active Principal</Text>
                <Text fontWeight="bold" color="green.600">₹{totalActivePrincipal}</Text>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Articles List */}
          <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={{ base: 3, md: 4 }}>
            <Text fontWeight="bold" mb={3}>All Articles</Text>
            <Box overflowX="auto">
              <table style={{ width: '100%', minWidth: '520px', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Name/No</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                    <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Total Principal</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Weight</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Status</th>
                    <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {articles.length > 0 ? articles.map(article => (
                    <tr key={article._id}>
                      <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{article.nameOrNo || 'N/A'}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(article.date)}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right', fontWeight: 'bold' }}>
                        ₹{article.totalPrincipal || article.amount || 0}
                      </td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{article.weight}g</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>
                        <Badge colorScheme={article.released ? 'green' : 'orange'}>
                          {article.released ? 'Released' : 'Active'}
                        </Badge>
                      </td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>
                        <Button
                          size="xs"
                          colorScheme="blue"
                          variant={viewArticle?._id === article._id ? 'solid' : 'ghost'}
                          onClick={() => setViewArticle(viewArticle?._id === article._id ? null : article)}
                        >
                          {viewArticle?._id === article._id ? 'Hide' : 'View'}
                        </Button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td style={{ padding: '8px' }} colSpan={6}>No articles found for this client.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          </Box>

          {/* Expanded Article Details */}
          {viewArticle && (
            <Box bg="white" borderWidth="1px" borderColor="blue.200" borderRadius="lg" p={{ base: 3, md: 4 }}>
              <Flex justify="space-between" align="center" mb={3}>
                <Text fontWeight="bold" fontSize="lg">Details: {viewArticle.nameOrNo}</Text>
                <Button size="xs" variant="ghost" onClick={() => setViewArticle(null)}>Close</Button>
              </Flex>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
                <Box><Text fontSize="xs" color="gray.500">Weight</Text><Text>{viewArticle.weight}g</Text></Box>
                <Box><Text fontSize="xs" color="gray.500">Interest Rate</Text><Text>{viewArticle.interestRate}%</Text></Box>
                <Box><Text fontSize="xs" color="gray.500">Address</Text><Text>{viewArticle.address || 'N/A'}</Text></Box>
                <Box><Text fontSize="xs" color="gray.500">Description</Text><Text>{viewArticle.description || 'N/A'}</Text></Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">ID Proofs</Text>
                  {viewArticle.identityProof && Array.isArray(viewArticle.identityProof) && viewArticle.identityProof.length > 0 ? (
                    viewArticle.identityProof.map((proof, idx) => (
                      <Text key={idx} fontSize="sm">• {proof.type}: {proof.number}</Text>
                    ))
                  ) : (
                    <Text fontSize="sm">N/A</Text>
                  )}
                </Box>
              </SimpleGrid>

              {/* Loan History */}
              <Text fontWeight="bold" mb={2}>Loan History</Text>
              <Box overflowX="auto" mb={4}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Type</th>
                      <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Amount</th>
                      <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Int. Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewArticle.loans && viewArticle.loans.length > 0 ? (
                      viewArticle.loans.map((loan, idx) => (
                        <tr key={`loan-${idx}`}>
                          <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(loan.date)}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{loan.description || (idx === 0 ? 'Initial' : 'Top-up')}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>₹{loan.amount}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>{loan.interestRate || viewArticle.interestRate}%</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(viewArticle.date)}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>Initial (Legacy)</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>₹{viewArticle.amount}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>{viewArticle.interestRate}%</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Box>

              {/* Payment History */}
              {viewArticle.payments && viewArticle.payments.length > 0 && (
                <>
                  <Text fontWeight="bold" mb={2}>Payment History</Text>
                  <Box overflowX="auto" mb={4}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Type</th>
                          <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Amount</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewArticle.payments.map((payment, idx) => (
                          <tr key={`payment-${idx}`}>
                            <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(payment.date)}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>
                              <Badge colorScheme={payment.type === 'interest' ? 'green' : 'blue'}>
                                {payment.type.toUpperCase()}
                              </Badge>
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>₹{payment.amount}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{payment.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </>
              )}

              {/* Interest + Balance Summary */}
              <Box pt={2} borderTop="1px solid #e2e8f0">
                <Flex justify="space-between" align="center">
                  <Flex align="center" gap={2}>
                    <Text fontWeight="bold">Interest Paid Upto:</Text>
                    <Text>{formatDate(viewArticle.interestPaidUpto) || 'N/A'}</Text>
                  </Flex>
                  {user?.role === 'admin' && viewArticle.payments && viewArticle.payments.length > 0 && (() => {
                    const lastInterestPayment = [...viewArticle.payments].reverse().find(p => p.type === 'interest');
                    return lastInterestPayment ? (
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="blue"
                        onClick={() => { setPaymentArticle(viewArticle); setEditingPayment(lastInterestPayment); }}
                      >
                        Edit
                      </Button>
                    ) : null;
                  })()}
                </Flex>
                <Flex justify="space-between" align="center" mt={2}>
                  <Text fontWeight="bold" fontSize="lg">Total Principal Balance:</Text>
                  <Text fontWeight="bold" fontSize="lg" color="green.600">₹{viewArticle.totalPrincipal}</Text>
                </Flex>
              </Box>
            </Box>
          )}
        </>
      )}

      {!selectedClient && !loading && (
        <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={8} textAlign="center">
          <Text color="gray.500">Search and select a client above to view their ledger.</Text>
        </Box>
      )}

      {/* Payment Edit Modal */}
      {editingPayment && paymentArticle && (
        <PaymentModal
          isOpen={Boolean(editingPayment)}
          onClose={() => { setEditingPayment(null); setPaymentArticle(null); }}
          article={paymentArticle}
          paymentToEdit={editingPayment}
          onPaymentSuccess={() => {
            refreshArticles();
            setEditingPayment(null);
            setPaymentArticle(null);
            // Refresh view article too
            if (viewArticle) {
              setTimeout(() => {
                // Re-select viewArticle from refreshed list
                setViewArticle(prev => {
                  const updated = articles.find(a => a._id === prev?._id);
                  return updated || prev;
                });
              }, 500);
            }
          }}
        />
      )}
    </Stack>
  );
};

export default Ledger;
